import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Trash2, Edit3, Package, Plus, Loader2, GripVertical, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const MyProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]); // Ab ye objects honge [{_id, name, ...}]
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [orderChanged, setOrderChanged] = useState(false);

  const fetchData = async () => {
    try {
      // 1. Categories aur Products dono parallel fetch karein
      const [prodRes, catRes] = await Promise.all([
        api.get('/products/my-products'),
        api.get('/categories')
      ]);

      setProducts(prodRes.data);
      setFilteredProducts(prodRes.data);
      setCategories(catRes.data); // Nayi categories list
      setLoading(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch data");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter Logic: Ab category ID ya Name se match karein
  useEffect(() => {
    if (activeCategory === 'All') {
      setFilteredProducts(products);
    } else {
      // product.category._id ya product.category.name se filter karein
      setFilteredProducts(products.filter(p => 
        p.category?._id === activeCategory || p.category?.name === activeCategory
      ));
    }
  }, [activeCategory, products]);

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    if (activeCategory !== 'All') {
        toast.error("Please select 'All' to reorder products");
        return;
    }

    const items = Array.from(products);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setProducts(items);
    setOrderChanged(true);
  };

  const saveOrder = async () => {
    const loadingToast = toast.loading("Saving order...");
    try {
      const updatedOrder = products.map((prod, index) => ({
        id: prod._id,
        sortOrder: index
      }));
      await api.put('/products/reorder', { products: updatedOrder });
      toast.success("Inventory order updated!", { id: loadingToast });
      setOrderChanged(false);
    } catch (err) {
      toast.error("Failed to save order", { id: loadingToast });
    }
  };

  const deleteProduct = (id) => {
    toast((t) => (
      <div className="flex flex-col gap-3 p-1">
        <p className="font-bold text-gray-800">Delete this item?</p>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                await api.delete(`/products/${id}`);
                setProducts(products.filter(p => p._id !== id));
                toast.success("Deleted!");
              } catch (err) { toast.error("Error deleting"); }
            }}
            className="bg-red-500 text-white px-4 py-2 rounded-xl text-xs font-bold"
          >
            Yes, Delete
          </button>
          <button onClick={() => toast.dismiss(t.id)} className="bg-gray-100 text-gray-600 px-4 py-2 rounded-xl text-xs font-bold">Cancel</button>
        </div>
      </div>
    ));
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64">
      <Loader2 className="animate-spin text-green-600" size={40} />
      <p className="mt-4 text-gray-500 font-bold italic">Loading Inventory...</p>
    </div>
  );

  return (
    <div className="space-y-6 pb-20 px-2 md:px-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-gray-900 tracking-tight">Inventory</h2>
          <p className="text-gray-500 font-medium italic">Sort and manage your shop items</p>
        </div>
        <div className="flex gap-3">
            {orderChanged && (
                <button onClick={saveOrder} className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-2xl font-bold shadow-lg animate-bounce">
                    <Check size={20} /> Save Order
                </button>
            )}
            <Link to="/dashboard/add-product" className="flex items-center gap-2 bg-black text-white px-6 py-3 rounded-2xl font-bold shadow-lg">
                <Plus size={20} /> Add Item
            </Link>
        </div>
      </div>

      {/* Category Chips - Naye Model se linked */}
      <div className="flex gap-2 overflow-x-auto pb-4 no-scrollbar">
        <button
          onClick={() => setActiveCategory('All')}
          className={`px-6 py-2.5 rounded-full text-sm font-black transition-all border ${
            activeCategory === 'All' ? "bg-black border-black text-white shadow-md" : "bg-white text-gray-500 border-gray-100"
          }`}
        >
          All Items
        </button>
        {categories.map((cat) => (
          <button
            key={cat._id}
            onClick={() => setActiveCategory(cat._id)}
            className={`px-6 py-2.5 rounded-full text-sm font-black whitespace-nowrap transition-all border ${
              activeCategory === cat._id ? "bg-green-600 border-green-600 text-white shadow-md shadow-green-100" : "bg-white text-gray-500 border-gray-100 hover:border-green-200"
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Products List */}
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId="products-list">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
              {filteredProducts.map((product, index) => (
                <Draggable 
                    key={product._id} 
                    draggableId={product._id} 
                    index={index}
                    isDragDisabled={activeCategory !== 'All'}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`bg-white p-4 rounded-[2rem] border transition-all ${
                        snapshot.isDragging ? "border-green-500 shadow-2xl scale-[1.02] z-50" : "border-gray-50 shadow-sm"
                      } flex gap-4 items-center`}
                    >
                      <div {...provided.dragHandleProps} className="text-gray-300 hover:text-gray-500 p-2"><GripVertical size={22} /></div>

                      <img src={product.images?.[0]} className="w-20 h-20 rounded-2xl object-cover bg-gray-50 border border-gray-100" alt="" />

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-1">
                           <div>
    <span className="text-[10px] font-black text-green-600 uppercase tracking-widest bg-green-50 px-2 py-0.5 rounded-md">
      {/* Agar product.category object hai to uska name dikhaye, warna 'General' */}
      {product.category && typeof product.category === 'object' 
        ? product.category.name 
        : 'General'}
    </span>
    <h4 className="font-bold text-gray-900 text-base md:text-lg truncate mt-1">
      {product.name}
    </h4>
</div>
                            <p className="font-black text-gray-900 text-xl">Rs. {Number(product.price).toLocaleString()}</p>
                        </div>
                        
                        <div className="flex gap-2 mt-3">
                            <Link to={`/dashboard/edit-product/${product._id}`} className="p-2.5 bg-gray-50 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all">
                                <Edit3 size={16} />
                            </Link>
                            <button onClick={() => deleteProduct(product._id)} className="p-2.5 bg-red-50 text-red-500 rounded-xl hover:bg-red-600 hover:text-white transition-all">
                                <Trash2 size={16} />
                            </button>
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {filteredProducts.length === 0 && (
        <div className="text-center py-20 bg-white rounded-[3rem] border border-dashed border-gray-200">
          <Package size={48} className="text-gray-100 mx-auto mb-4" />
          <p className="text-gray-400 font-bold text-lg">No products found in this category</p>
        </div>
      )}
    </div>
  );
};

export default MyProducts;